import type { Memo, MemoDataEntry } from '../api/types';
import { getAgentName } from '../utils/agentUtils';

export interface MemosJsonResult {
  Activity: {
    UUID: string;
    TriId: string;
    Title: string;
    Type: string;
    Appellation: string;
  };
  WHAT: {
    MaterialEntity: {
      UUID: string;
      Appellation: string;
      Type: string;
      Material: string;
      Identifier: string;
              Provenance: {
          Location: {
            UUID: string;
            Site: string;
            Address: string;
            Post_code: string;
            Place: string;
            Country: string;
            GeoRef: {
              latitude: number;
              longitude: number;
              geoname_id: string;
            }
          };
          Sublocation: string;
          Zoning: string;
          Floor: string;
          Orientation: string;
          Nomenclature: string;
        }
    } | null;
    DigitalEntity: {
      In: {
        UUID: string;
        Appelation: string;
        Description: string;
        Type: string;
        Format: string;
        URL: string;
        Version: string;
        copyright: string;
        de_link: string;
        file_spec: string;
        owner: string;
        user: string;
      } | null;
      Out: {
        UUID: string;
        Appelation: string;
        Description: string;
        Type: string;
        Format: string;
        URL: string;
        Version: string;
        copyright: string;
        de_link: string;
        file_spec: string;
        owner: string;
        user: string;
      } | null;
    }
  }| null;
  WHY: {
    UUID: string;
    Title: string;
    Acronym: string;
    Type: string;
    AurehalId: string;
    Description: string;
    FundedBy: string;
    ReferenceSci: string;
    InstrumentationDescription: string;
    InstrumentationLink: string;
    
    
  } | null;
  WHO: Array<{
    UUID: string;
    Name: string;
    AgentAurehalId: string;
    Affiliation: string;
    AffiliationAurehalId: string;
    Group: string;
    Roles: string;
  }> | null;
  WHERE: {         
    UUID: string;
    Site: string;
    Address: string;
    Post_code: string;
    Place: string;
    Country: string;
    GeoRef: {
      latitude: number;
      longitude: number;
      geoname_id: string;
    }
  } | null;
  WHEN: {
    Start_time: string;
    End_time: string;
    Time_zone: string;
    Time_link: string;
  } | null;

  MEMoS: {
    Version: string;
    URL: string;
    GeneratedBy: string;
    GeneratedAt: string;
  };
  [key: string]: unknown;
}

export function useMemosJsonGenerator(): {
  generateMemosJson: (memo: Memo) => MemosJsonResult;
} {

  const buildSectionHierarchy = (dataEntries: MemoDataEntry[]): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    dataEntries.forEach(entry => {
      if (!entry.name) return;
      const parts = entry.name.split('.');
      let current = result;
      
      // Parcourir chaque niveau de la hiérarchie
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        
        // Si c'est le dernier élément, on assigne la valeur
        if (i === parts.length - 1) {
          current[part] = entry.value;
        } else {
          // Sinon on crée ou on utilise l'objet existant
          current[part] = current[part] || {};
          current = current[part] as Record<string, unknown>;
        }
      }
    });

    return result;
  };

  const generateMemosJson = (memo: Memo): MemosJsonResult => {
    try {
      // Construire le JSON MEMoS directement à partir de l'objet memo
      const memosJson: MemosJsonResult = {
        Activity: {
          Title: memo.title,
          UUID: memo.uuid,
          TriId: memo.triid,
          Type: memo.schema_model?.type || '',
          Appellation: memo.schema_model?.appellation || '',
        },
        WHAT: {
          MaterialEntity: memo.material_entity ? {
            UUID: memo.material_entity.uuid,
            Appellation: memo.material_entity.appellation,
            Type: memo.material_entity.type || '',
            Material: memo.material_entity.material || '',
            Identifier: memo.material_entity.identifier || '',
            Provenance: {
              Location: {
                UUID: memo.material_entity.provenance_location?.uuid || '',
                Site: memo.material_entity.provenance_location?.is_confidential ? '[CONFIDENTIEL]' : (memo.material_entity.provenance_location?.site || ''),
                Address: memo.material_entity.provenance_location?.is_confidential ? '' : (memo.material_entity.provenance_location?.address || ''),
                Post_code: memo.material_entity.provenance_location?.is_confidential ? '' : (memo.material_entity.provenance_location?.post_code || ''),
                Place: memo.material_entity.provenance_location?.is_confidential ? '' : (memo.material_entity.provenance_location?.place || ''),
                Country: memo.material_entity.provenance_location?.is_confidential ? '' : (memo.material_entity.provenance_location?.country || ''),
                GeoRef: {
                  latitude: memo.material_entity.provenance_location?.is_confidential ? 0 : (memo.material_entity.provenance_location?.latitude || 0),
                  longitude: memo.material_entity.provenance_location?.is_confidential ? 0 : (memo.material_entity.provenance_location?.longitude || 0),
                  geoname_id: memo.material_entity.provenance_location?.is_confidential ? '' : (memo.material_entity.provenance_location?.geoname_id || '')
                }
              },
              Sublocation: memo.material_entity.provenance_interior_exterior || '',
              Zoning: memo.material_entity.provenance_zoning || '',
              Floor: memo.material_entity.provenance_floor || '',

              Orientation: memo.material_entity.provenance_orientation || '',
              Nomenclature: memo.material_entity.provenance_nomenclature || '',
            }
          } : null,
          DigitalEntity: {
            In: memo.digital_entity_input ? {
              UUID: memo.digital_entity_input.uuid,
              Appelation: memo.digital_entity_input.appellation,
              Description: memo.digital_entity_input.description || '',
              Type: memo.digital_entity_input.type || '',
              Format: memo.digital_entity_input.format || '',
              URL: memo.digital_entity_input.url || '',
              Version: memo.digital_entity_input.version || '',
              copyright: memo.digital_entity_input.copyright || '',
              de_link: memo.digital_entity_input.de_link || '',
              file_spec: memo.digital_entity_input.file_spec || '',
              owner: memo.digital_entity_input.owner || '',
              user: memo.digital_entity_input.user || '',
            } : null,
            Out: memo.digital_entity_output ? {
              UUID: memo.digital_entity_output.uuid,
              Appelation: memo.digital_entity_output.appellation,
              Description: memo.digital_entity_output.description || '',
              Type: memo.digital_entity_output.type || '',
              Format: memo.digital_entity_output.format || '',
              URL: memo.digital_entity_output.url || '',
              Version: memo.digital_entity_output.version || '',
              copyright: memo.digital_entity_output.copyright || '',
              de_link: memo.digital_entity_output.de_link || '',
              file_spec: memo.digital_entity_output.file_spec || '',
              owner: memo.digital_entity_output.owner || '',
              user: memo.digital_entity_output.user || '',
            } : null,
          }
        },
        WHY: {
          UUID: memo.project?.uuid || '',
          Title: memo.project?.title || '',
          Acronym: memo.project?.acronym || '',
          Type: memo.project?.type || '',
          AurehalId: memo.project?.aurehal_id || '',
          Description: memo.project?.description || '',
          FundedBy: memo.project?.fundedby || '',
                      ReferenceSci: memo.project?.link || '',
            InstrumentationDescription: memo.instrumentation_description || '',
            InstrumentationLink: memo.instrumentation_link || '',
        },
        WHO: (memo.agents_entries || []).map(entry => ({
          UUID: entry.agent.uuid,
          Name: getAgentName(entry.agent),
          AgentAurehalId: entry.agent.idref_id || '',
          Affiliation: entry.agent.affiliation || '',
          AffiliationAurehalId: entry.agent.affiliation_id || '',
          Group: entry.agent.group || '',
          Roles: entry.roleslist || ''
        })),
        WHERE: {
          UUID: memo.location?.uuid || '',
          Site: memo.location?.is_confidential ? '[CONFIDENTIEL]' : (memo.location?.site || ''),
          Address: memo.location?.is_confidential ? '' : (memo.location?.address || ''),
          Post_code: memo.location?.is_confidential ? '' : (memo.location?.post_code || ''),
          Place: memo.location?.is_confidential ? '' : (memo.location?.place || ''),
          Country: memo.location?.is_confidential ? '' : (memo.location?.country || ''),
          GeoRef: {
            latitude: memo.location?.is_confidential ? 0 : (memo.location?.latitude || 0),
            longitude: memo.location?.is_confidential ? 0 : (memo.location?.longitude || 0),
            geoname_id: memo.location?.is_confidential ? '' : (memo.location?.geoname_id || '')
          }
        },
        WHEN: {
          Start_time: memo.start_time || '',
          End_time: memo.end_time || '',
          Time_zone: memo.time_zone || '',
          Time_link: memo.time_link || '',
        },
        // Ajouter les sections dynamiques (HOW, WHICH)
        ...buildSectionHierarchy(memo.data_entries || []),

        MEMoS: {
          Version: '2.0',
          URL: 'https://github.com/MAP-TEAM/MEMoS',
          GeneratedBy: memo.user_id,
          GeneratedAt: memo.updated_at,
        }
      };

      return memosJson;
    } catch (error) {
      console.error('Erreur lors de la génération du JSON MEMoS:', error);
      throw error;
    }
  };

  return {
    generateMemosJson
  };
} 